import { useState, type JSX } from 'react';

import { cn } from '@/lib/utils.js';

import { fileExt } from './file-ext.js';

interface AttachmentChipBaseProps {
  name: string;
  thumbUrl?: string | null;
  className?: string;
  /**
   * The image failed to resolve (the bearer-authed fetch errored). Distinguishes a permanent
   * failure from the loading state (thumbUrl still null) so the chip shows an error placeholder
   * instead of spinning forever. Used by the image variants.
   */
  failed?: boolean;
  /**
   * The bytes will not come for this reader — the byte fetch answered 404. Its own state, not a flavour
   * of {@link failed}: "this is as good as it gets" and "couldn't load it, try again" are different
   * answers for the reader, and only one of them is worth retrying. Wins over `failed` when both are set.
   */
  unavailable?: boolean;
  /** Upload state from S3 pre-signed flow. Present on draft/card variants only. */
  uploadState?: 'uploading' | 'uploaded' | 'error';
  uploadError?: string;
}

interface AttachmentChipDraftProps extends AttachmentChipBaseProps {
  variant: 'draft';
  /**
   * Absent while the file is still on its way into the workspace: there is nothing to take back yet —
   * the upload cannot be aborted — and a cross that does nothing is worse than no cross.
   */
  onRemove?: () => void;
  onContextMenu?: (e: React.MouseEvent<HTMLElement>) => void;
}

interface AttachmentChipSentProps extends AttachmentChipBaseProps {
  variant: 'sent';
  onOpen: () => void;
}

interface AttachmentChipCardProps extends AttachmentChipBaseProps {
  variant: 'card';
  onOpen: () => void;
  onRemove: () => void;
  onContextMenu?: (e: React.MouseEvent<HTMLElement>) => void;
  /**
   * Where the file comes from, when that is not the plain case of bytes the user just picked off their
   * own disk. A referenced file has no thumbnail to recognise it by, so without this a workspace file and
   * a SharePoint file are the same grey tile.
   */
  source?: string;
}

interface AttachmentChipBubbleProps extends AttachmentChipBaseProps {
  variant: 'bubble';
}

interface AttachmentChipSentCardProps extends AttachmentChipBaseProps {
  variant: 'sent-card';
  onOpen: () => void;
}

/** Large square preview for sent images in the thread (ChatGPT-style proportions). */
interface AttachmentChipSentHeroProps extends AttachmentChipBaseProps {
  variant: 'sent-hero';
  onOpen: () => void;
}

type AttachmentChipProps =
  | AttachmentChipDraftProps
  | AttachmentChipSentProps
  | AttachmentChipCardProps
  | AttachmentChipBubbleProps
  | AttachmentChipSentCardProps
  | AttachmentChipSentHeroProps;

function FileIcon({ name }: { name: string }): JSX.Element {
  const isImage = /\.(png|jpe?g|gif|webp)$/i.test(name);
  return isImage ? (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
    >
      <rect x="3" y="3" width="18" height="18" rx="2" />
      <circle cx="9" cy="9" r="2" />
      <path d="M21 15l-5-5L5 21" />
    </svg>
  ) : (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
    >
      <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" />
      <polyline points="14 2 14 8 20 8" />
    </svg>
  );
}

function chipGlyph(name: string, thumbUrl?: string | null): JSX.Element {
  if (thumbUrl != null) {
    return (
      <img
        className="size-full rounded-md object-cover"
        src={thumbUrl}
        alt=""
        aria-hidden="true"
      />
    );
  }
  return (
    <span className="text-mini font-semibold uppercase" aria-hidden="true">
      {fileExt(name)}
    </span>
  );
}

function renderDraft(props: AttachmentChipDraftProps): JSX.Element {
  const { name, thumbUrl, uploadState, onRemove, onContextMenu, className } =
    props;
  const uploading = uploadState === 'uploading';
  return (
    <span
      className={cn(
        'flex max-w-[15rem] items-center gap-2 rounded-md border border-border/60 bg-card py-1.5 pl-1.5 pr-2 text-xs text-foreground',
        className,
      )}
      onContextMenu={onContextMenu}
    >
      <span className="flex size-7 shrink-0 items-center justify-center rounded-md bg-primary/10 text-primary">
        {uploading ? (
          <Spinner className="size-4" />
        ) : thumbUrl != null ? (
          <img
            className="size-full rounded-md object-cover"
            src={thumbUrl}
            alt=""
            aria-hidden="true"
          />
        ) : (
          <FileIcon name={name} />
        )}
      </span>
      <span
        className={cn(
          'min-w-0 flex-1 truncate font-medium',
          uploading && 'text-muted-foreground',
        )}
        title={name}
      >
        {name}
      </span>
      {onRemove && (
        <button
          type="button"
          className="shrink-0 text-muted-foreground transition-colors hover:text-destructive"
          onClick={onRemove}
          aria-label={`Remove ${name}`}
          title={`Remove ${name}`}
        >
          <svg
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          >
            <path d="M18 6L6 18M6 6l12 12" />
          </svg>
        </button>
      )}
    </span>
  );
}

function renderSent(props: AttachmentChipSentProps): JSX.Element {
  const { name, thumbUrl, onOpen, className } = props;
  return (
    <button
      type="button"
      className={cn(
        'flex max-w-[15rem] cursor-pointer items-center gap-2 rounded-md border border-border/60 bg-card py-1.5 pl-1.5 pr-2 text-xs text-foreground transition-colors hover:border-primary/40 hover:bg-primary/5',
        className,
      )}
      onClick={onOpen}
      aria-label={`Open attachment ${name}`}
    >
      <span className="flex size-7 shrink-0 items-center justify-center rounded-md bg-primary/10 text-primary">
        {chipGlyph(name, thumbUrl)}
      </span>
      <span className="min-w-0 flex-1 truncate font-medium">{name}</span>
    </button>
  );
}

function Spinner({ className }: { className?: string }): JSX.Element {
  return (
    <svg
      className={cn('animate-spin text-primary/60', className ?? 'size-5')}
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden="true"
      focusable={false}
    >
      <circle
        className="opacity-20"
        cx="12"
        cy="12"
        r="10"
        stroke="currentColor"
        strokeWidth="3"
      />
      <path
        className="opacity-80"
        fill="currentColor"
        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
      />
    </svg>
  );
}

function ErrorGlyph(): JSX.Element {
  return (
    <span className="flex flex-col items-center justify-center gap-1 text-destructive/70">
      <svg
        width="18"
        height="18"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        aria-hidden="true"
        focusable={false}
      >
        <circle cx="12" cy="12" r="10" />
        <path d="M12 8v4M12 16h.01" />
      </svg>
      <span className="text-nano font-medium">Failed</span>
    </span>
  );
}

/**
 * Placeholder for a file this reader cannot get the bytes of. Deliberately not the {@link ErrorGlyph}:
 * there is nothing to retry, and the message that referenced the file stays as it was sent.
 *
 * The copy says only that the file is not available to this reader. The byte route answers 404 both for a
 * file that was deleted and for a workspace artifact the reader may not read — that is on purpose, so
 * that a restricted file's existence never leaks — so naming either reason would be a guess, and naming
 * the second one would leak exactly what the 404 is hiding.
 */
function UnavailableGlyph(): JSX.Element {
  return (
    <span className="flex flex-col items-center justify-center gap-1 text-muted-foreground">
      <svg
        width="18"
        height="18"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        aria-hidden="true"
        focusable={false}
      >
        <circle cx="12" cy="12" r="10" />
        <path d="M4.9 4.9l14.2 14.2" />
      </svg>
      <span className="text-nano font-medium">Not available to you</span>
    </span>
  );
}

/**
 * A resolved image: shows a spinner while the browser decodes it and an error placeholder if it
 * fails to load, then fades the image in. Fills its positioned parent; `imgClassName` controls the
 * fit (cover for thumbs, contain for the hero).
 */
function ResolvedImage({
  src,
  name,
  imgClassName,
}: {
  src: string;
  name: string;
  imgClassName?: string;
}): JSX.Element {
  const [status, setStatus] = useState<'loading' | 'loaded' | 'error'>(
    'loading',
  );
  return (
    <>
      {status === 'loading' && (
        <span className="absolute inset-0 flex items-center justify-center">
          <Spinner />
        </span>
      )}
      {status === 'error' && (
        <span className="absolute inset-0 flex items-center justify-center">
          <ErrorGlyph />
        </span>
      )}
      <img
        className={cn(
          imgClassName ?? 'size-full object-cover',
          'transition-opacity duration-200',
          status !== 'loaded' && 'opacity-0',
        )}
        src={src}
        alt={name}
        onLoad={() => setStatus('loaded')}
        onError={() => setStatus('error')}
      />
    </>
  );
}

function renderCard(props: AttachmentChipCardProps): JSX.Element {
  const {
    name,
    thumbUrl,
    uploadState,
    source,
    onOpen,
    onRemove,
    onContextMenu,
    className,
  } = props;
  const isUploading = uploadState === 'uploading';
  return (
    <span
      className={cn(
        'group relative inline-flex size-20 shrink-0 rounded-md border border-border/60 bg-muted',
        className,
      )}
      onContextMenu={onContextMenu}
      title={name}
    >
      {/* Clickable preview area */}
      <button
        type="button"
        className="relative flex size-full overflow-hidden rounded-md items-center justify-center"
        onClick={onOpen}
        aria-label={`Preview ${name}`}
      >
        {thumbUrl != null ? (
          <ResolvedImage key={thumbUrl} src={thumbUrl} name={name} />
        ) : (
          <span className="flex flex-col items-center gap-1 text-muted-foreground">
            <FileIcon name={name} />
            <span className="max-w-[4rem] truncate px-1 text-micro font-medium">
              {name}
            </span>
          </span>
        )}
      </button>

      {/* Where the file comes from, for a chip that only references one */}
      {source !== undefined && (
        <span className="pointer-events-none absolute inset-x-0 bottom-0 truncate rounded-b-md bg-foreground/70 px-1 text-center text-nano font-medium text-background">
          {source}
        </span>
      )}

      {/* Upload progress overlay */}
      {isUploading && (
        <span className="pointer-events-none absolute inset-0 flex items-center justify-center rounded-md bg-black/40 backdrop-blur-[2px]">
          <svg
            className="size-5 animate-spin text-white"
            viewBox="0 0 24 24"
            fill="none"
          >
            <circle
              className="opacity-20"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="3"
            />
            <path
              className="opacity-90"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
            />
          </svg>
        </span>
      )}

      {/* Remove button — always there on touch, hover-revealed where there is a pointer */}
      <button
        type="button"
        className="absolute -right-2 -top-2 flex size-5 items-center justify-center rounded-full bg-black/60 text-white opacity-100 shadow-md backdrop-blur-sm transition-opacity [@media(hover:hover)]:opacity-0 group-hover:opacity-100 focus-visible:opacity-100 hover:bg-black/80"
        onClick={onRemove}
        aria-label={`Remove ${name}`}
        title={`Remove ${name}`}
      >
        <svg
          width="10"
          height="10"
          viewBox="0 0 10 10"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
        >
          <path d="M2 2l6 6M8 2l-6 6" />
        </svg>
      </button>
    </span>
  );
}

function extBadgeColor(name: string): string {
  const ext = fileExt(name).toLowerCase();
  if (ext === 'pdf') return '#e74c3c';
  if (['doc', 'docx'].includes(ext)) return '#2980b9';
  if (['xls', 'xlsx', 'csv'].includes(ext)) return '#27ae60';
  if (['ppt', 'pptx'].includes(ext)) return '#e67e22';
  if (['zip', 'rar', '7z', 'tar', 'gz'].includes(ext)) return '#8e44ad';
  if (['mp4', 'mov', 'avi', 'webm'].includes(ext)) return '#c0392b';
  if (['mp3', 'wav', 'ogg', 'flac'].includes(ext)) return '#16a085';
  if (['js', 'ts', 'tsx', 'jsx', 'py', 'go', 'rs'].includes(ext))
    return '#f39c12';
  if (['json', 'xml', 'yaml', 'yml', 'toml'].includes(ext)) return '#3498db';
  if (['sql', 'db', 'sqlite', 'sqli'].includes(ext)) return '#2c3e50';
  if (['log', 'ini', 'cfg', 'conf', 'env'].includes(ext)) return '#6c757d';
  return '#7f8c8d';
}

/**
 * Sent images in the thread. Always an image (the caller gates on the mime type), so instead of the
 * file-extension badge it shows a fixed-size framed placeholder with a spinner while the bearer-authed
 * url resolves and the browser decodes it — then swaps in the image. A placeholder replaces the spinner
 * if the bytes never arrive, so an unfetchable object never spins forever: "Not available to you" when
 * they will not come at all, the error glyph when the fetch merely failed.
 */
function renderSentHero(props: AttachmentChipSentHeroProps): JSX.Element {
  const { name, thumbUrl, failed, unavailable, onOpen, className } = props;
  const ready = thumbUrl != null;
  const label = ready
    ? `Open ${name}`
    : unavailable
      ? `${name} — not available to you`
      : failed
        ? `${name} — failed to load`
        : `${name} — loading`;
  return (
    <button
      type="button"
      onClick={ready ? onOpen : undefined}
      aria-label={label}
      aria-disabled={!ready}
      disabled={unavailable || failed}
      title={name}
      className={cn(
        'group relative items-center justify-center shrink-0 overflow-hidden rounded-3xl border border-border/60 bg-muted transition-opacity hover:opacity-90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40',
        ready
          ? 'inline-flex w-auto max-w-[min(100%,18rem)] max-h-[min(45vh,18rem)]'
          : 'flex aspect-square w-full max-w-[min(100%,14rem)]',
        className,
      )}
    >
      {ready ? (
        <ResolvedImage
          key={thumbUrl}
          src={thumbUrl}
          name={name}
          imgClassName="h-auto max-h-[min(45vh,18rem)] w-auto max-w-full object-contain"
        />
      ) : unavailable ? (
        <UnavailableGlyph />
      ) : failed ? (
        <ErrorGlyph />
      ) : (
        <Spinner className="size-8" />
      )}
    </button>
  );
}

function renderSentCard(props: AttachmentChipSentCardProps): JSX.Element {
  const { name, thumbUrl, failed, unavailable, onOpen, className } = props;
  const color = extBadgeColor(name);
  const ext = fileExt(name);
  const label = unavailable
    ? `${name} — not available to you`
    : failed
      ? `${name} — failed to load`
      : `Open ${name}`;
  return (
    <button
      type="button"
      onClick={unavailable || failed ? undefined : onOpen}
      aria-label={label}
      aria-disabled={unavailable || failed}
      disabled={unavailable || failed}
      title={name}
      className={cn(
        'group relative inline-flex size-20 shrink-0 flex-col overflow-hidden rounded-md border border-border/60 bg-muted transition-opacity hover:opacity-90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40',
        className,
      )}
    >
      {unavailable ? (
        <UnavailableGlyph />
      ) : failed ? (
        <ErrorGlyph />
      ) : thumbUrl != null ? (
        <img className="size-full object-cover" src={thumbUrl} alt={name} />
      ) : (
        <>
          {/* Colored header strip with file extension badge */}
          <span
            className="flex w-full flex-1 items-center justify-center text-caption font-bold uppercase tracking-wide text-white"
            style={{ background: color }}
          >
            {ext || '?'}
          </span>
          {/* Filename footer */}
          <span className="flex w-full items-center bg-card px-1.5 py-1">
            <span className="w-full truncate text-micro font-medium text-foreground/70">
              {name}
            </span>
          </span>
        </>
      )}
    </button>
  );
}

function renderBubble(props: AttachmentChipBubbleProps): JSX.Element {
  const { name, thumbUrl, className } = props;
  return (
    <div
      className={cn(
        'inline-flex max-w-full items-center gap-2 rounded-md bg-white/15 px-2.5 py-1.5 text-xs text-primary-foreground',
        className,
      )}
    >
      {thumbUrl != null ? (
        <img
          className="size-3.5 rounded object-cover"
          src={thumbUrl}
          alt=""
          aria-hidden="true"
        />
      ) : (
        <FileIcon name={name} />
      )}
      <span className="truncate font-medium">{name}</span>
    </div>
  );
}

export function AttachmentChip(props: AttachmentChipProps): JSX.Element {
  switch (props.variant) {
    case 'draft':
      return renderDraft(props);
    case 'sent':
      return renderSent(props);
    case 'card':
      return renderCard(props);
    case 'bubble':
      return renderBubble(props);
    case 'sent-card':
      return renderSentCard(props);
    case 'sent-hero':
      return renderSentHero(props);
  }
}
